import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import { V2PrescriptionCategoryModel } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { OptionPickerComponent, PickerOption } from './option-picker.component';

/**
 * The categories a line is filed under: what is taken, gathered as chips that each take themselves
 * back off, over a searchable list of the whole dictionary that stays open for the next pick.
 *
 * Only the dictionary adds, but a taken category is removed by the code it carries — so one the
 * dictionary no longer offers can still be taken off a line that was filed under it. The dictionary
 * is cached in the service for the session; a failure leaves the list empty rather than blocking a
 * line that can be filed later.
 */
@Component({
  selector: 'guarantees-category-picker',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe, OptionPickerComponent],
  template: `
    @if (value().length > 0) {
      <ul class="flex flex-wrap gap-1">
        @for (category of value(); track category.categoryCode) {
          <li>
            <button
              class="inline-flex items-center gap-1 rounded-full border border-primary-500 bg-primary-200 px-2 py-0.5 text-sm font-semibold text-primary-900 transition-colors duration-150 hover:bg-primary-300 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
              type="button"
              [matTooltip]="'guaranteeV2.action.removeCategory' | translate"
              (click)="remove(category.categoryCode)">
              {{ category.categoryName }}
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">close</mat-icon>
            </button>
          </li>
        }
      </ul>
    }

    <guarantees-option-picker
      wrap
      multiple
      [options]="options()"
      [values]="codes()"
      [placeholder]="(loading() ? 'guaranteeV2.message.categoriesLoading' : 'guaranteeV2.message.categoryPlaceholder') | translate"
      [emptyMessage]="'guaranteeV2.message.categoryNoMatch' | translate"
      [disabled]="options().length === 0"
      (valueChange)="toggle($event)"></guarantees-option-picker>
  `,
  host: { class: 'flex flex-col gap-1.5' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryPickerComponent {
  /** The categories taken, carried whole: the record stores the code beside the name. */
  value = input<V2PrescriptionCategoryModel[]>([]);
  valueChange = output<V2PrescriptionCategoryModel[]>();

  private readonly dictionaries = inject(DictionariesApiService);
  private readonly dictionary = signal<V2PrescriptionCategoryModel[]>([]);
  protected readonly loading = signal(true);

  /** The dictionary whole: what is already taken is ticked in the list rather than taken out of it. */
  protected readonly options = computed<PickerOption[]>(() => this.dictionary().map(category => ({ value: String(category.categoryCode), label: category.categoryName })));
  protected readonly codes = computed(() => this.value().map(category => String(category.categoryCode)));

  constructor() {
    this.dictionaries
      .getPrescriptionCategories()
      .pipe(take(1), takeUntilDestroyed(inject(DestroyRef)))
      .subscribe({
        next: categories => {
          this.dictionary.set(categories.map(category => ({ categoryCode: category.code, categoryName: category.nameUa })));
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected toggle(code: string): void {
    const taken = this.value().find(category => String(category.categoryCode) === code);
    if (taken) {
      this.remove(taken.categoryCode);

      return;
    }

    const picked = this.dictionary().find(category => String(category.categoryCode) === code);
    if (picked) {
      this.valueChange.emit([...this.value(), picked]);
    }
  }

  protected remove(code: number): void {
    this.valueChange.emit(this.value().filter(category => category.categoryCode !== code));
  }
}
